import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CTAButton from "@/components/ui/CTAButton";
import { normalizeImgUrl } from "@/lib/utils-img";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";
import useParallax from "@/hooks/useParallax";

const EYEBROW = "Delegación de Evangelización Digital · Diócesis de Huelva";

const SLUG_DEFAULTS = {
  noticias: {
    title: "Noticias y Actualidad",
    title_italic: "de la Evangelización Digital",
    subtitle: EYEBROW,
    description: "Mantente al día de todo lo que acontece en nuestra delegación y en el mundo de la evangelización digital",
    bg_image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1920&q=85",
    overlay_opacity: 0.55,
  },
  formacion: {
    title: "Formación",
    title_italic: "para Evangelizadores Digitales",
    subtitle: EYEBROW,
    description: "Formamos a agentes pastorales, sacerdotes, catequistas y jóvenes para ser evangelizadores digitales competentes y auténticos",
    bg_image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&q=85",
    overlay_opacity: 0.55,
    cta_primary_text: "Ver cursos",
    cta_primary_url: "#cursos",
  },
  eventos: {
    title: "Eventos · Cursos",
    title_italic: "y Encuentros Diocesanos",
    subtitle: EYEBROW,
    description: "Talleres, jornadas, asambleas y encuentros de evangelizadores digitales. Todo lo que necesitas para crecer en tu misión",
    bg_image: "https://images.unsplash.com/photo-1765947382559-93260e5d6c89?w=1920&q=85",
    overlay_opacity: 0.55,
  },
  recursos: {
    title: "Recursos",
    title_italic: "Material Evangelizador",
    subtitle: EYEBROW,
    description: "Guías, manuales, plantillas y documentos eclesiales para equiparte en la evangelización digital",
    bg_image: "https://images.unsplash.com/photo-1671774635688-e02ae66974df?w=1920&q=85",
    overlay_opacity: 0.55,
  },
  contacto: {
    title: "Contacto",
    title_italic: "Estamos Aquí para Ayudarte",
    subtitle: EYEBROW,
    description: "Escríbenos para cualquier consulta, propuesta de colaboración o solicitud de formación",
    bg_image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=85",
    overlay_opacity: 0.55,
  },
  "quienes-somos": {
    title: "Quiénes Somos",
    title_italic: "La Delegación Diocesana de Evangelización Digital",
    subtitle: EYEBROW,
    description: "Nacemos del impulso misionero de la Iglesia para llevar el Evangelio a los nuevos areópagos del siglo XXI: el entorno digital",
    bg_image: "https://images.unsplash.com/photo-1683199804561-ce75c2c7bf61?w=1920&q=85",
    overlay_opacity: 0.55,
    cta_primary_text: "Únete como Misionero",
    cta_primary_url: "/misionero-digital",
  },
  podcast: {
    title: "Podcast",
    title_italic: "Nuestra Voz en la Red",
    subtitle: EYEBROW,
    description: "Escucha nuestros episodios sobre evangelización, testimonios y formación digital para la misión de hoy",
    bg_image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1920&q=85",
    overlay_opacity: 0.55,
  },
};

export { EYEBROW, SLUG_DEFAULTS };

/**
 * Barra flotante ("localnav") de página interior.
 *
 * Píldora fija bajo el navbar con el título de la página y dos accesos
 * rápidos, inspirada en la isla flotante de las páginas de producto de
 * apple.com (brief-apple.md §5). Aparece cuando el héroe deja de estar en
 * pantalla — no antes, para no duplicar el <h1> que ya se ve — y se queda
 * fija mientras se lee el resto de la página.
 *
 * Solo en escritorio (md+): en móvil el espacio vertical ya es escaso y el
 * propio héroe es corto, así que la píldora aparecería casi pegada a él.
 *
 * Se renderiza con un portal a `document.body`, no en su sitio natural del
 * árbol. `<PageTransition>` envuelve cada página en un `div.page-transition`
 * cuya animación de entrada dispara `animation-fill-mode: both`; al
 * terminar, `getComputedStyle` sigue devolviendo una matriz de identidad en
 * vez de `none`. Cualquier `position:fixed` dentro de ese div — como esta
 * píldora — pasa a posicionarse respecto a él en vez de respecto al
 * viewport, y con la página desplazada eso la manda fuera de pantalla. El
 * portal la saca de ese árbol sin dejar de estar controlada por el estado
 * de este componente.
 */
function BarraFlotante({ visible, title }) {
  return createPortal(
    <div
      inert={!visible}
      className={`hidden md:flex fixed left-0 right-0 z-40 justify-center px-6 pointer-events-none transition-[opacity,transform] duration-base ease-out-apple ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
      style={{ top: "104px" }}
    >
      <div className="navbar-glass ring-hairline-dark pointer-events-auto w-full max-w-5xl h-[52px] rounded-[18px] flex items-center justify-between gap-4 px-5">
        <p className="text-[19px] font-semibold text-[#f5f5f7] tracking-tight truncate">{title}</p>
        <div className="flex items-center gap-3 flex-shrink-0">
          <CTAButton to="/contacto" variant="outlineLight" size="sm">Contactar</CTAButton>
          <CTAButton to="/misionero-digital" variant="primary" size="sm">Ser Misionero</CTAButton>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Cabecera de página.
 *
 * Dos cambios de fondo respecto a la versión anterior:
 *
 *  1. **No bloquea.** Antes esperaba la respuesta del backend y, encima,
 *     retenía el pintado hasta que `new Image()` terminaba de descargar el
 *     fondo. Eran dos esperas encadenadas contra un servicio que en el plan
 *     gratuito de Render puede estar dormido, y mientras tanto la página era
 *     un rectángulo gris pulsante. Ahora se pinta al instante con los valores
 *     por defecto y el contenido del CMS entra encima cuando llega.
 *  2. **El fondo es una `<img>` real**, no un `background-image` de CSS. El
 *     preload scanner del navegador no ve las imágenes declaradas en CSS, así
 *     que la del héroe —la más grande y la primera que se ve— era justo la
 *     última en pedirse.
 *
 * Acepta `slug` (busca el héroe publicado de esa página) o `content` directo,
 * que es lo que usan las páginas que antes se dibujaban el héroe a mano.
 */
export default function PageHero({ slug, content: contentProp, children }) {
  const porDefecto = SLUG_DEFAULTS[slug] || {};

  const { data } = useCachedResource(
    slug && !contentProp ? `page:${slug}` : null,
    () => (slug && !contentProp ? apiGet(`/pages/${slug}`) : Promise.resolve(null)),
    [slug]
  );

  const heroGuardado = data?.sections?.find(s => s.type === "hero" && s.visible !== false)?.content;
  const c = contentProp || { ...porDefecto, ...(heroGuardado || {}) };

  const bgRef = useParallax(0.25);
  const imagen = c.bg_image ? normalizeImgUrl(c.bg_image) : null;
  const opacity = c.overlay_opacity ?? 0.55;

  // Se activa cuando el héroe deja de estar en pantalla (con el hueco de la
  // cabecera fija ya descontado vía `rootMargin`), para que la barra flotante
  // entre justo cuando el título del héroe desaparece bajo ella, no antes.
  const heroRef = useRef(null);
  const [pasadoHero, setPasadoHero] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setPasadoHero(!entry.isIntersecting),
      { rootMargin: "-104px 0px 0px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <section
        ref={heroRef}
        data-testid={`page-hero-${slug || "custom"}`}
        className="relative flex items-end justify-center overflow-hidden bg-[#1d1d1f]"
        style={{ minHeight: "420px" }}
      >
        {imagen && (
          <div ref={bgRef} className="absolute inset-0 will-change-transform">
            <img
              src={imagen}
              alt=""
              aria-hidden="true"
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Velo oscuro (neutro, estilo Apple) */}
        <div className="absolute inset-0 bg-[#1d1d1f]" style={{ opacity: imagen ? opacity : 1 }} />

        <div className="relative z-10 w-full text-white px-6 md:px-12 pt-36 pb-16 max-w-5xl mx-auto">
          {c.subtitle && (
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#86868b] mb-4 animate-fadeIn">
              {c.subtitle}
            </p>
          )}
          <h1
            className="font-heading font-bold mb-4 animate-fadeInUp text-balance"
            style={{ fontSize: "var(--text-h1)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
          >
            {c.title}
            {c.title_italic && (
              <>
                <br />
                <span className="font-spiritual italic font-normal text-white/60">{c.title_italic}</span>
              </>
            )}
          </h1>
          {c.description && (
            <p className="text-base sm:text-xl text-[#a1a1a6] max-w-2xl mt-4 leading-snug animate-fadeInUp animation-delay-200">
              {c.description}
            </p>
          )}
          {(c.cta_primary_text || c.cta_secondary_text) && (
            <div className="flex flex-col sm:flex-row gap-4 mt-8 animate-fadeInUp animation-delay-400">
              {c.cta_primary_text && (
                <CTAButton to={c.cta_primary_url || "#"} variant="light" size="lg">
                  {c.cta_primary_text}
                </CTAButton>
              )}
              {c.cta_secondary_text && (
                <CTAButton to={c.cta_secondary_url || "#"} variant="outlineLight" size="lg">
                  {c.cta_secondary_text}
                </CTAButton>
              )}
            </div>
          )}
          {children && <div className="mt-8 animate-fadeInUp animation-delay-400">{children}</div>}
        </div>
      </section>

      {c.title && <BarraFlotante visible={pasadoHero} title={c.title} />}
    </>
  );
}
